import type { ModelName, MutationMethodForModel, ParamsForMethod, ReturnForMethod } from "../../types";
import { getMutationSideEffectUpdaters } from "./get-mutation-side-effect-updaters";
import { splitMutationSideEffectUpdaterTypes } from "./split-mutation-side-effect-updater-types";
import type { SideEffectConfigContext } from "./types";

export interface GetMutationSideEffectsWithUpdatersArgs<
  Model extends ModelName,
  Method extends MutationMethodForModel<Model>,
> {
  model: Model;
  method: Method;
  request: ParamsForMethod<Model, Method>;
  response?: ReturnForMethod<Model, Method>;
  context?: SideEffectConfigContext;
}

/**
 * Retrieves the configured side effects from AUTOMATIC_MUTATION_SIDE_EFFECT_CONFIGS for a given model and mutation
 * method splitting them by if they have an updater or not.
 *
 * @template Model - The Tellescope model name (e.g., "users", "appointments")
 * @template Method - The mutation method for the model (e.g., "create_user", "update_appointment")
 *
 * @param args.model - The Tellescope model name
 * @param args.method - The mutation method to retrieve side effects for
 * @param args.request - The request for the specified mutation (from tanstack)
 * @param [args.response] - The response for the specified mutation (from tanstack)
 * @param [args.context] - Additional context (e.g., session) for side effect config constructors
 */
export const getAllMutationSideEffects = <Model extends ModelName, Method extends MutationMethodForModel<Model>>({
  model,
  method,
  request,
  response,
  context,
}: GetMutationSideEffectsWithUpdatersArgs<Model, Method>) => {
  const config = getMutationSideEffectUpdaters({ model, method })?.(request, response, context);
  return splitMutationSideEffectUpdaterTypes<Model>(config);
};
